import { Box, Button, CircularProgress, Divider, Grid, InputLabel, TextField, Typography } from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import React, { FC, useEffect, useMemo, useState } from "react";
import { sortBy } from "lodash";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import WifiIcon from "@mui/icons-material/Wifi";
import DownloadIcon from "@mui/icons-material/Download";
import TaskIcon from "@mui/icons-material/Task";
import ErrorIcon from "@mui/icons-material/Error";
import WifiOffIcon from "@mui/icons-material/WifiOff";

import "../configuration.css";
import {
	useGetCameraStatusQuery,
	useGetCamerasListQuery,
	useGetDeviceStatusQuery,
	useGetSiteConfigQuery,
	useGetSystemStatusQuery
} from "../../../redux/reducers/settings.reducer";
import { DEVICE_STATUS_REFRESH_INTERVAL } from "../../../utils/constants";
import downloadServices from "../../../redux/services/download.services";
import { LoadingButton } from "@mui/lab";
import { downloadFile } from "../../../utils/commonUtils";
import { IGetCameraStatusRequestData } from "../../../interfaces";
import { useAppSelector } from "../../../redux";

interface ISystemTemperatureDetails {
	temperature: string;
	current: string;
	maximum: string;
}

interface IMemoryUsageDetails {
	type: string;
	current: string;
	total: string;
}

type SoftwareStatusType = "running" | "stopped" | "unknown";

interface ISoftwareStatusDetails {
	software: string;
	status: SoftwareStatusType;
	uptime: string;
}

type CameraStatusType = "running" | "loading" | "stopped";

interface ICameraStatusDetails {
	camera_name: string;
	status: CameraStatusType;
}

const DeviceSettings: FC = () => {
	const authState = useAppSelector((state) => state.auth);

	const [isDeviceOnline, setIsDeviceOnline] = useState<boolean>(true);

	const [diagnosticsReportFile, setDiagnosticsReportFile] = useState<{ fileName: string; fileBlob: Blob } | null>(null);
	const [diagnosticsGenerationStatus, setDiagnosticsGenerationStatus] = useState<
		"pending" | "running" | "generated" | "failed"
	>("pending");

	// APIS
	const { data: siteConfig } = useGetSiteConfigQuery(null, {
		skip: !authState.user.token || authState.user.token === "invalid"
	});
	const { data: deviceStatusResponse, isLoading: getDeviceStatusLoading } = useGetDeviceStatusQuery(null, {
		pollingInterval: DEVICE_STATUS_REFRESH_INTERVAL * 1000
	});
	const { data: systemStatusResponse, isLoading: getSystemStatusLoading } = useGetSystemStatusQuery(null, {
		pollingInterval: DEVICE_STATUS_REFRESH_INTERVAL * 1000
	});
	const { data: getCamerasListResponse } = useGetCamerasListQuery({});

	const systemTemperatureTableData = useMemo<ISystemTemperatureDetails[]>(() => {
		const temperatureDetails: ISystemTemperatureDetails[] = [];

		const deviceStatus = deviceStatusResponse?.data;

		if (deviceStatus) {
			if (deviceStatus.detail[1] && deviceStatus.detail[1].GPU) {
				temperatureDetails.push({
					temperature: "MCPU",
					current: `${deviceStatus.detail[1]?.GPU[0] ?? 0}\u00b0C`,
					maximum: `${deviceStatus.detail[1]?.GPU[1] ?? 0}\u00b0C`
				});
			}

			temperatureDetails.push({
				temperature: "Thermal",
				current:
					deviceStatus.detail[1] && deviceStatus.detail[1].Thermal
						? `${deviceStatus.detail[1]?.Thermal[0] ?? 0}\u00b0C`
						: "N/A",
				maximum:
					deviceStatus.detail[1] && deviceStatus.detail[1].Thermal
						? `${deviceStatus.detail[1]?.Thermal[1] ?? 0}\u00b0C`
						: "N/A"
			});
		}

		return temperatureDetails;
	}, [deviceStatusResponse?.data]);

	const memoryUsageTableData = useMemo<IMemoryUsageDetails[]>(() => {
		const memoryUsageDetails: IMemoryUsageDetails[] = [];

		const deviceStatus = deviceStatusResponse?.data;

		if (deviceStatus) {
			memoryUsageDetails.push({
				type: "RAM",
				current:
					deviceStatus.detail[2] && deviceStatus.detail[2].Ram
						? `${deviceStatus.detail[2]?.Ram[0]?.toFixed(1) ?? 0} GB`
						: "N/A",
				total:
					deviceStatus.detail[2] && deviceStatus.detail[2].Ram
						? `${deviceStatus.detail[2]?.Ram[1]?.toFixed(1) ?? 0} GB`
						: "N/A"
			});

			memoryUsageDetails.push({
				type: "SWAP",
				current:
					deviceStatus.detail[2] && deviceStatus.detail[2].Swap
						? `${deviceStatus.detail[2]?.Swap[0]?.toFixed(1) ?? 0} GB`
						: "N/A",
				total:
					deviceStatus.detail[2] && deviceStatus.detail[2].Swap
						? `${deviceStatus.detail[2]?.Swap[1]?.toFixed(1) ?? 0} GB`
						: "N/A"
			});

			memoryUsageDetails.push({
				type: "Device",
				current:
					deviceStatus.detail[2] && deviceStatus.detail[2].Device
						? `${deviceStatus.detail[2]?.Device[0]?.toFixed(1) ?? 0} GB`
						: "N/A",
				total:
					deviceStatus.detail[2] && deviceStatus.detail[2].Device
						? `${deviceStatus.detail[2]?.Device[1]?.toFixed(1) ?? 0} GB`
						: "N/A"
			});

			if (deviceStatus.detail[2] && deviceStatus.detail[2].External) {
				memoryUsageDetails.push({
					type: "External",
					current: `${deviceStatus.detail[2]?.External[0]?.toFixed(1) ?? 0} GB`,
					total: `${deviceStatus.detail[2]?.External[1]?.toFixed(1) ?? 0} GB`
				});
			}
		}

		return memoryUsageDetails;
	}, [deviceStatusResponse?.data]);

	const softwareStatusTableData = useMemo<ISoftwareStatusDetails[]>(() => {
		const softwareStatusDetails: ISoftwareStatusDetails[] = [];

		const systemStatus = systemStatusResponse?.data;

		if (systemStatus) {
			softwareStatusDetails.push(
				{
					software: "Software 1",
					status: systemStatus.detail.db ? ((systemStatus.detail.db[0] ?? "stopped") as SoftwareStatusType) : "unknown",
					uptime: systemStatus.detail.db ? systemStatus.detail.db[1] ?? "" : ""
				},
				{
					software: "Software 2",
					status: systemStatus.detail.ml_engine
						? ((systemStatus.detail.ml_engine[0] ?? "stopped") as SoftwareStatusType)
						: "unknown",
					uptime: systemStatus.detail.ml_engine ? systemStatus.detail.ml_engine[1] ?? "" : ""
				},
				{
					software: "Software 3",
					status: systemStatus.detail.nginx
						? ((systemStatus.detail.nginx[0] ?? "stopped") as SoftwareStatusType)
						: "unknown",
					uptime: systemStatus.detail.nginx ? systemStatus.detail.nginx[1] ?? "" : ""
				},
				{
					software: "Software 4",
					status: systemStatus.detail.redis
						? ((systemStatus.detail.redis[0] ?? "stopped") as SoftwareStatusType)
						: "unknown",
					uptime: systemStatus.detail.redis ? systemStatus.detail.redis[1] ?? "" : ""
				},
				{
					software: "Software 5",
					status: systemStatus.detail.ui ? ((systemStatus.detail.ui[0] ?? "stopped") as SoftwareStatusType) : "unknown",
					uptime: systemStatus.detail.ui ? systemStatus.detail.ui[1] ?? "" : ""
				},
				{
					software: "Software 6",
					status: systemStatus.detail.utils
						? ((systemStatus.detail.utils[0] ?? "stopped") as SoftwareStatusType)
						: "unknown",
					uptime: systemStatus.detail.utils ? systemStatus.detail.utils[1] ?? "" : ""
				},
				{
					software: "Software 7",
					status: systemStatus.detail.web
						? ((systemStatus.detail.web[0] ?? "stopped") as SoftwareStatusType)
						: "unknown",
					uptime: systemStatus.detail.web ? systemStatus.detail.web[1] ?? "" : ""
				}
			);
		}

		return softwareStatusDetails;
	}, [systemStatusResponse?.data]);

	const camerasList = useMemo(() => getCamerasListResponse?.results ?? [], [getCamerasListResponse?.results]);
	const getCameraStatusRequestData = useMemo<IGetCameraStatusRequestData>(() => {
		const requestData: IGetCameraStatusRequestData = {};

		if (getCamerasListResponse) {
			for (const cameraItem of getCamerasListResponse.results) {
				requestData[cameraItem.camera_id] = {
					rtsp_uri: cameraItem.rtsp_uri,
					fetch_frame: false
				};
			}
		}

		return requestData;
	}, [getCamerasListResponse]);

	const {
		data: cameraStatus,
		isLoading: getCameraStatusLoading,
		isFetching: getCameraStatusFetching
	} = useGetCameraStatusQuery(getCameraStatusRequestData, {
		skip: Object.keys(getCameraStatusRequestData).length <= 0
	});

	function handleRunDiagnosticsButtonClick() {
		setDiagnosticsGenerationStatus("running");
		downloadServices
			.handleRunDiagnostics()
			.then((response) => {
				setDiagnosticsReportFile(response);
				setDiagnosticsGenerationStatus("generated");
			})
			.catch((error) => {
				console.error("Run diagnostics failed ::", error);
				setDiagnosticsGenerationStatus("failed");
				setTimeout(() => {
					setDiagnosticsGenerationStatus("pending");
				}, 3000);
			});
	}

	function handleDownloadReportButtonClick() {
		if (diagnosticsReportFile) {
			downloadFile(diagnosticsReportFile.fileBlob, diagnosticsReportFile.fileName);
			setDiagnosticsGenerationStatus("pending");
		}
	}

	useEffect(() => {
		const handleOnlineEvent = () => setIsDeviceOnline(true);
		const handleOfflineEvent = () => setIsDeviceOnline(false);

		window.addEventListener("online", handleOnlineEvent);
		window.addEventListener("offline", handleOfflineEvent);

		return () => {
			window.removeEventListener("online", handleOnlineEvent);
			window.removeEventListener("offline", handleOfflineEvent);
		};
	}, []);

	function getCameraStatusRowsData(): ICameraStatusDetails[] {
		if (!camerasList) return [];

		const cameraStatusList: ICameraStatusDetails[] = camerasList.map((cameraItem) => {
			let status: CameraStatusType = "loading";

			if (cameraStatus && cameraStatus[cameraItem.camera_id] && cameraStatus[cameraItem.camera_id].status) {
				status = "running";
			} else if (getCameraStatusLoading || getCameraStatusFetching) {
				status = "loading";
			} else {
				status = "stopped";
			}

			return {
				camera_name: cameraItem.camera_site,
				status: status
			};
		});

		return sortBy(cameraStatusList, "camera_name");
	}

	const systemTemperatureColumnsData: GridColDef[] = [
		{
			field: "temperature",
			headerName: "Temperature",
			flex: 1,
			sortable: false,
			cellClassName: "device-settings-table-cell border",
			align: "center",
			headerAlign: "center"
		},
		{
			field: "current",
			headerName: "Current",
			flex: 1,
			sortable: false,
			cellClassName: "device-settings-table-cell border",
			align: "center",
			headerAlign: "center"
		},
		{
			field: "maximum",
			headerName: "Maximum",
			flex: 1,
			sortable: false,
			cellClassName: "device-settings-table-cell",
			align: "center",
			headerAlign: "center"
		}
	];

	const memoryUsageColumnsData: GridColDef[] = [
		{
			field: "type",
			headerName: "Type",
			flex: 1,
			sortable: false,
			cellClassName: "device-settings-table-cell border",
			align: "center",
			headerAlign: "center"
		},
		{
			field: "current",
			headerName: "Current",
			flex: 1,
			sortable: false,
			cellClassName: "device-settings-table-cell border",
			align: "center",
			headerAlign: "center"
		},
		{
			field: "total",
			headerName: "Total",
			flex: 1,
			sortable: false,
			cellClassName: "device-settings-table-cell",
			align: "center",
			headerAlign: "center"
		}
	];

	const softwareStatusColumnsData: GridColDef[] = [
		{
			field: "software",
			headerName: "Software",
			flex: 1,
			sortable: false,
			cellClassName: "device-settings-table-cell border",
			align: "center",
			headerAlign: "center"
		},
		{
			field: "status",
			headerName: "Status",
			flex: 1,
			sortable: false,
			cellClassName: "device-settings-table-cell border",
			headerAlign: "center",
			renderCell: (params: GridRenderCellParams<ISoftwareStatusDetails, SoftwareStatusType>) => (
				<Box sx={{ width: "100%", height: "100%", paddingY: "0.5rem" }}>
					<Box className={`health-check-table-status-cell ${params.value}`}>
						<Typography variant="body2" textAlign="center" textTransform="capitalize">
							{params.value}
						</Typography>
					</Box>
				</Box>
			)
		},
		{
			field: "uptime",
			headerName: "Uptime",
			flex: 1,
			sortable: false,
			cellClassName: "device-settings-table-cell",
			align: "center",
			headerAlign: "center"
		}
	];

	const cameraStatusColumnsData: GridColDef[] = [
		{
			field: "camera_name",
			headerName: "Camera Name",
			flex: 1,
			sortable: false,
			cellClassName: "device-settings-table-cell border",
			align: "center",
			headerAlign: "center"
		},
		{
			field: "status",
			headerName: "Status",
			flex: 1,
			sortable: false,
			cellClassName: "device-settings-table-cell",
			headerAlign: "center",
			renderCell: (params: GridRenderCellParams<ICameraStatusDetails, CameraStatusType>) => (
				<Box sx={{ width: "100%", height: "100%", paddingY: "0.5rem" }}>
					<Box className={`health-check-table-status-cell ${params.value}`}>
						{params.value === "loading" ? (
							<CircularProgress size={20} />
						) : (
							<Typography variant="body2" textAlign="center" textTransform="capitalize">
								{params.value}
							</Typography>
						)}
					</Box>
				</Box>
			)
		}
	];

	return (
		<Box className="configuration-screen-wrapper flex-wrapper">
			<Box className="device-settings-section">
				<Typography variant="h6" color="var(--color-primary-main)">
					Device Information
				</Typography>

				<Grid container spacing={2}>
					<Grid item xs={12} md={5} xl={3}>
						<InputLabel htmlFor="serial-number-input" sx={{ fontWeight: 500, marginBottom: "1rem" }}>
							Serial Number
						</InputLabel>

						<TextField
							fullWidth
							variant="outlined"
							color="primary"
							id="serial-number-input"
							value={siteConfig?.device_uuid ?? ""}
							InputProps={{ readOnly: true }}
							autoComplete="off"
						/>
					</Grid>

					{/* <Grid item xs={12} md={4} xl={3}>
						<InputLabel htmlFor="device-id-input" sx={{ fontWeight: 500, marginBottom: "1rem" }}>
							Device ID
						</InputLabel>

						<TextField
							fullWidth
							variant="outlined"
							color="primary"
							id="device-id-input"
							value={siteConfig?.device_uuid ?? ""}
							InputProps={{ readOnly: true }}
						/>
					</Grid> */}

					<Grid item xs={12} md={5} xl={3}>
						<InputLabel htmlFor="system-version-input" sx={{ fontWeight: 500, marginBottom: "1rem" }}>
							System Version
						</InputLabel>

						<TextField
							fullWidth
							variant="outlined"
							color="primary"
							id="system-version-input"
							value={siteConfig?.release_version ?? ""}
							InputProps={{ readOnly: true }}
							autoComplete="off"
						/>
					</Grid>

					<Box sx={{ width: "100%", display: { xs: "none", md: "block", xl: "none" } }} />

					<Grid item xs={12} md={5} xl={3}>
						<InputLabel htmlFor="local-ip-address-input" sx={{ fontWeight: 500, marginBottom: "1rem" }}>
							Local IP Address
						</InputLabel>

						<TextField
							fullWidth
							variant="outlined"
							color="primary"
							id="local-ip-address-input"
							value={siteConfig?.local_ipv4_address ?? ""}
							InputProps={{ readOnly: true }}
							autoComplete="off"
						/>
					</Grid>

					<Grid item xs={12} md={5} xl={3}>
						<InputLabel htmlFor="global-ip-address-input" sx={{ fontWeight: 500, marginBottom: "1rem" }}>
							Global IP
						</InputLabel>

						<TextField
							fullWidth
							variant="outlined"
							color="primary"
							id="global-ip-address-input"
							value={siteConfig?.global_ipv4_address ?? ""}
							InputProps={{ readOnly: true }}
							autoComplete="off"
						/>
					</Grid>
				</Grid>
			</Box>

			<Box className="device-settings-section">
				<Typography variant="h6" color="var(--color-primary-main)">
					Health Check
				</Typography>

				<Grid container spacing={2}>
					<Grid item xs={12} md={6}>
						<Typography fontWeight={500}>System Temperature</Typography>

						<Divider sx={{ margin: "1rem 0" }} />

						<Box className="device-health-check-table-wrapper">
							<DataGrid
								getRowId={(item) => item.temperature}
								sx={{ height: "250px" }}
								columns={systemTemperatureColumnsData}
								rows={systemTemperatureTableData}
								disableRowSelectionOnClick
								disableColumnMenu
								columnHeaderHeight={60}
								loading={systemTemperatureTableData.length <= 0 && getDeviceStatusLoading}
								classes={{
									columnSeparator: "device-settings-table-column-separator",
									columnHeader: "device-settings-table-column-headers",
									virtualScroller: "device-settings-table-virtual-scroller"
								}}
								slots={{ footer: () => <></> }}
							/>
						</Box>
					</Grid>

					<Grid item xs={12} md={6}>
						<Typography fontWeight={500}>Memory Usage</Typography>

						<Divider sx={{ margin: "1rem 0" }} />

						<Box className="device-health-check-table-wrapper">
							<DataGrid
								getRowId={(item) => item.type}
								sx={{ height: "250px" }}
								columns={memoryUsageColumnsData}
								rows={memoryUsageTableData}
								disableRowSelectionOnClick
								disableColumnMenu
								columnHeaderHeight={60}
								loading={memoryUsageTableData.length <= 0 && getDeviceStatusLoading}
								classes={{
									columnSeparator: "device-settings-table-column-separator",
									columnHeader: "device-settings-table-column-headers",
									virtualScroller: "device-settings-table-virtual-scroller"
								}}
								slots={{ footer: () => <></> }}
							/>
						</Box>
					</Grid>

					<Grid item xs={12} md={6}>
						<Typography fontWeight={500}>Software Status</Typography>

						<Divider sx={{ margin: "1rem 0" }} />

						<Box className="device-health-check-table-wrapper">
							<DataGrid
								getRowId={(item) => item.software}
								sx={{ height: "250px" }}
								columns={softwareStatusColumnsData}
								rows={softwareStatusTableData}
								disableRowSelectionOnClick
								disableColumnMenu
								columnHeaderHeight={60}
								loading={softwareStatusTableData.length <= 0 && getSystemStatusLoading}
								classes={{
									columnSeparator: "device-settings-table-column-separator",
									columnHeader: "device-settings-table-column-headers",
									virtualScroller: "device-settings-table-virtual-scroller"
								}}
								slots={{ footer: () => <></> }}
							/>
						</Box>
					</Grid>

					<Grid item xs={12} md={6}>
						<Typography fontWeight={500}>Camera Status</Typography>

						<Divider sx={{ margin: "1rem 0" }} />

						<Box className="device-health-check-table-wrapper">
							<DataGrid
								getRowId={(item) => item.camera_name}
								sx={{ height: "250px" }}
								columns={cameraStatusColumnsData}
								rows={getCameraStatusRowsData()}
								disableRowSelectionOnClick
								disableColumnMenu
								columnHeaderHeight={60}
								classes={{
									columnSeparator: "device-settings-table-column-separator",
									columnHeader: "device-settings-table-column-headers",
									virtualScroller: "device-settings-table-virtual-scroller"
								}}
								slots={{ footer: () => <></> }}
							/>
						</Box>
					</Grid>
				</Grid>
			</Box>

			<Box className="device-settings-section">
				<Typography variant="h6" color="var(--color-primary-main)">
					Diagnostics
				</Typography>

				{diagnosticsGenerationStatus === "generated" ? (
					<Button
						variant="contained"
						color="primary"
						startIcon={<DownloadIcon />}
						onClick={handleDownloadReportButtonClick}
					>
						Download Report
					</Button>
				) : (
					<LoadingButton
						variant="contained"
						color="primary"
						startIcon={<RestartAltIcon />}
						onClick={handleRunDiagnosticsButtonClick}
						loading={diagnosticsGenerationStatus === "running"}
						loadingPosition="start"
					>
						Run Diagnostics
					</LoadingButton>
				)}

				{diagnosticsGenerationStatus === "pending" ? (
					isDeviceOnline ? (
						<Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
							<WifiIcon fontSize="small" color="success" />
							<Typography variant="body2" color="var(--color-success-main)">
								Connected to Internet
							</Typography>
						</Box>
					) : (
						<Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
							<WifiOffIcon fontSize="small" color="error" />
							<Typography variant="body2" color="var(--color-error-main)">
								Not Connected to Internet
							</Typography>
						</Box>
					)
				) : diagnosticsGenerationStatus === "running" ? (
					<Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
						<RestartAltIcon fontSize="small" color="primary" />
						<Typography variant="body2" color="var(--color-primary-main)">
							Running Diagnostics
						</Typography>
					</Box>
				) : diagnosticsGenerationStatus === "generated" ? (
					<Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
						<TaskIcon color="success" />
						<Typography variant="body2" color="var(--color-success-main)">
							Diagnostics Report Generated
						</Typography>
					</Box>
				) : diagnosticsGenerationStatus === "failed" ? (
					<Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
						<ErrorIcon color="error" />
						<Typography variant="body2" color="var(--color-error-main)">
							Diagnostics Report Generation Failed
						</Typography>
					</Box>
				) : null}
			</Box>
		</Box>
	);
};

export default DeviceSettings;
